import { BaseExportService } from './BaseExportService';

/**
 * Individual Liveness Export Service
 * 
 * Handles data export for Individual Liveness Verification reports
 */
export class IndividualLivenessExportService extends BaseExportService {
  constructor(data, metadata = {}) {
    super(data, metadata);
    
    // Extract liveness data structure
    this.result = this.data?.result || {};
    this.results = this.result?.results || {};
    this.summary = this.result?.summary || {};
  }

  get_filename_prefix() {
    const timestamp = Date.now().toString().slice(-8);
    return `Individual_Liveness_Verification_${timestamp}`;
  }

  get_available_sections() {
    return {
      summary: 'Liveness Summary',
      quality_assessment: 'Image Quality Assessment',
      liveness_detection: 'Liveness Detection',
      screen_detection: 'Screen Detection',
      integrated_analysis: 'Integrated Analysis',
      raw_json: 'Raw JSON Data',
    };
  }

  get_default_export_options() {
    return {
      summary: false,
      quality_assessment: false,
      liveness_detection: false,
      screen_detection: false,
      integrated_analysis: false,
      raw_json: false,
    };
  }

  prepare_export_data(export_options) {
    const export_data = {};

    if (export_options.summary) {
      export_data.summary = this.prepare_summary();
    }

    if (export_options.quality_assessment) {
      export_data.quality_assessment = this.prepare_quality_assessment();
    }

    if (export_options.liveness_detection) {
      export_data.liveness_detection = this.prepare_liveness_detection();
    }

    if (export_options.screen_detection) {
      export_data.screen_detection = this.prepare_screen_detection();
    }

    if (export_options.integrated_analysis) {
      export_data.integrated_analysis = this.prepare_integrated_analysis();
    }

    if (export_options.raw_json) {
      export_data.raw_json = [this.prepare_raw_json_data()];
    }

    return export_data;
  }

  /**
   * Prepare liveness verification summary
   */
  prepare_summary() {
    return [{
      'Final Decision': this.summary.final_decision || 'N/A',
      'Confidence Score': this.summary.confidence || 'N/A',
      'Is Live': this.summary.is_live || false,
      'Base Liveness Score': this.summary.base_liveness_score || 'N/A',
      'Adjusted Liveness Score': this.summary.adjusted_liveness_score || 'N/A',
      'Timestamp': this.result.timestamp || 'N/A',
    }];
  }

  /**
   * Prepare image quality assessment data
   */
  prepare_quality_assessment() {
    const quality = this.results.quality_assessment || {};
    const metrics = quality.metrics || {};
    
    return [{
      'Overall Score': quality.overall_score || 'N/A',
      'Quality Level': quality.quality_level || 'N/A',
      'Is Suitable': quality.is_suitable || false,
      'Sharpness': metrics.sharpness || 'N/A',
      'Lighting': metrics.lighting || 'N/A',
      'Noise': metrics.noise || 'N/A',
      'Contrast': metrics.contrast || 'N/A',
      'Resolution': metrics.resolution || 'N/A',
      'Face Size': metrics.face_size || 'N/A',
      'Recommendations': Array.isArray(quality.recommendations) ? 
                        quality.recommendations.join('; ') : 'N/A',
      'Processing Time (ms)': quality.processing_time_ms || 'N/A',
    }];
  }

  /**
   * Prepare liveness detection results
   */
  prepare_liveness_detection() {
    const liveness = this.results.liveness_detection || {};
    const detection_methods = liveness.detection_methods || {};
    const texture_details = liveness.texture_details || {};
    
    return [{
      'Is Live': liveness.is_live || false,
      'Confidence Score': liveness.confidence_score || 'N/A',
      'Liveness Score': liveness.liveness_score || 'N/A',
      'Status': liveness.status || 'N/A',
      'Texture Analysis': detection_methods.texture_analysis || 'N/A',
      'DeepFace AntiSpoof': detection_methods.deepface_antispoof || 'N/A',
      'Laplacian': detection_methods.laplacian || 'N/A',
      'Saturation': detection_methods.saturation || 'N/A',
      'Edge Density': detection_methods.edge_density || 'N/A',
      'Texture': detection_methods.texture || 'N/A',
      'High Frequency': detection_methods.high_freq || 'N/A',
      'Skin Color': detection_methods.skin_color || 'N/A',
      'Texture Laplacian': texture_details.laplacian || 'N/A',
      'Texture Saturation': texture_details.saturation || 'N/A',
      'Texture Edge Density': texture_details.edge_density || 'N/A',
      'Texture High Freq': texture_details.high_freq || 'N/A',
      'Texture Skin Color': texture_details.skin_color || 'N/A',
      'Processing Time (ms)': liveness.processing_time_ms || 'N/A',
    }];
  }

  /**
   * Prepare screen detection results
   */
  prepare_screen_detection() {
    const screen = this.results.screen_detection || {};
    const artifact_scores = screen.artifact_scores || {};
    
    return [{
      'Is Screen Likely': screen.is_screen_likely || false,
      'Confidence Score': screen.confidence_score || 'N/A',
      'Moire': artifact_scores.moire || 'N/A',
      'Pixel Patterns': artifact_scores.pixel_patterns || 'N/A',
      'Reflections': artifact_scores.reflections || 'N/A',
      'Screen Edges': artifact_scores.screen_edges || 'N/A',
      'Color Banding': artifact_scores.color_banding || 'N/A',
      'Compression': artifact_scores.compression || 'N/A',
      'Detected Artifacts': Array.isArray(screen.detected_artifacts) ? 
                           screen.detected_artifacts.join('; ') : 'N/A',
      'Recommendations': Array.isArray(screen.recommendations) ? 
                        screen.recommendations.join('; ') : 'N/A',
      'Processing Time (ms)': screen.processing_time_ms || 'N/A',
    }];
  }

  /**
   * Prepare integrated analysis results
   */
  prepare_integrated_analysis() {
    const integrated = this.results.integrated_analysis || {};
    
    return [{
      'Base Liveness Score': integrated.base_liveness_score || 'N/A',
      'Adjusted Liveness Score': integrated.adjusted_liveness_score || 'N/A',
      'Quality Factor': integrated.quality_factor || 'N/A',
      'Screen Factor': integrated.screen_factor || 'N/A',
      'Final Decision': integrated.final_decision || 'N/A',
      'Confidence': integrated.confidence || 'N/A',
      'Is Live': integrated.is_live || false,
      'Threshold Used': integrated.threshold_used || 'N/A',
      'Recommendations': Array.isArray(integrated.recommendations) ? 
                        integrated.recommendations.join('; ') : 'N/A',
    }];
  }

  /**
   * Prepare raw JSON data
   */
  prepare_raw_json_data() {
    return {
      status: this.data?.status || 'N/A',
      result: this.result,
    };
  }
}
